import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users, RefreshCw, LayoutDashboard } from 'lucide-react';
import { AdminService, supabase } from '../../services/adminService';

const toYMD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const fmt = (v) => (v === null || v === undefined ? '—' : String(v));
const fmtDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
};

const parseLocalDayRange = (fromYmd, toYmd) => {
  const from = fromYmd ? new Date(`${fromYmd}T00:00:00`) : null;
  const to = toYmd ? new Date(`${toYmd}T23:59:59.999`) : null;
  if (from && to && from > to) return { from: to, to: from };
  if (from && !to) return { from, to: new Date(`${fromYmd}T23:59:59.999`) };
  if (!from && to) return { from: new Date(`${toYmd}T00:00:00`), to };
  return { from, to };
};

const isWithinRange = (ts, from, to) => {
  if (!from && !to) return true;
  if (!ts) return false;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return false;
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
};

// Default Team Lead (Mansi tomar) selected when the page is opened without ?tl=...
const DEFAULT_TEAM_LEAD_ID = '38c97b74-2c62-43bb-bd8e-99333dabbf63';

const WFHEmployeeReport = () => {
  const [sp, setSp] = useSearchParams();
  const [selectedTeamLeadId, setSelectedTeamLeadId] = useState(() => sp.get('tl') || DEFAULT_TEAM_LEAD_ID);
  const [selectedBatchName, setSelectedBatchName] = useState(() => sp.get('batch') || '');
  const [fromDate, setFromDate] = useState(() => sp.get('from') || toYMD(new Date()));
  const [toDate, setToDate] = useState(() => sp.get('to') || toYMD(new Date()));
  const [batches, setBatches] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoadingRows, setIsLoadingRows] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTeamLeadId) params.set('tl', selectedTeamLeadId);
    if (selectedBatchName) params.set('batch', selectedBatchName);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    setSp(params, { replace: true });
  }, [selectedTeamLeadId, selectedBatchName, fromDate, toDate]);

  const { data: teamLeads = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['adminTeamLeads'],
    queryFn: AdminService.getTeamLeads,
    staleTime: 5 * 60 * 1000,
  });

  const { data: telecallers = [] } = useQuery({
    queryKey: ['adminTelecallers'],
    queryFn: () => AdminService.getTelecallers({ status: 'all' }),
    staleTime: 5 * 60 * 1000,
  });

  // Stats can include IDs that are no longer "telecaller" role (left / role changed).
  // Resolve those separately so the table shows name instead of raw UUID.
  const [resolvedUsersById, setResolvedUsersById] = useState(() => new Map());

  const telecallersById = useMemo(() => {
    const map = new Map();
    telecallers.forEach((u) => map.set(String(u.id), u));
    teamLeads.forEach((u) => map.set(String(u.id), u));
    resolvedUsersById.forEach((u, id) => {
      if (!map.has(id)) map.set(id, u);
    });
    return map;
  }, [telecallers, teamLeads, resolvedUsersById]);

  const loadBatches = async () => {
    if (!selectedTeamLeadId) {
      setBatches([]);
      setSelectedBatchName('');
      return;
    }
    const { data, error } = await supabase.rpc('admin_wfh_list_batches', { p_team_lead_id: selectedTeamLeadId });
    if (error) {
      console.error('Failed to load WFH batches:', error);
      setBatches([]);
      return;
    }
    setBatches(data || []);
  };

  const loadRows = async () => {
    if (!selectedTeamLeadId) return;
    setIsLoadingRows(true);
    try {
      // Prefer server-side aggregation (fast) when available.
      const { data, error } = await supabase.rpc('admin_wfh_employee_stats', {
        p_team_lead_id: selectedTeamLeadId,
        p_batch_name: selectedBatchName || null,
        p_from_date: fromDate || null,
        p_to_date: toDate || null,
      });

      const rpcRows = Array.isArray(data) ? data : [];
      if (!error) {
        // Backward compatible: older RPC versions won't return confirmed_count.
        // Keep the rows (so the report doesn't go blank) and default confirmed_count to 0.
        const hasConfirmedField =
          rpcRows.length === 0 ? true : Object.prototype.hasOwnProperty.call(rpcRows[0], 'confirmed_count');
        if (!hasConfirmedField) {
          console.warn('admin_wfh_employee_stats RPC missing confirmed_count; please deploy latest RPC.');
        }
        setRows(
          rpcRows.map((r) => ({
            ...r,
            confirmed_count: Number(r?.confirmed_count) || 0,
          }))
        );
        return;
      }

      // Fallback: client-side aggregation using raw WFH leads (works only if RLS/policies allow reads).
      console.warn('admin_wfh_employee_stats RPC failed; falling back to client-side aggregation:', error);

      const pageSize = 1000;
      let pageFrom = 0;
      const all = [];

      while (true) {
        let q = supabase
          .from('wfh_leads')
          .select('assigned_to,claimed_by,disposition_updated_by,disposition,claimed_at,disposition_updated_at,team_lead_id,batch_name')
          .eq('team_lead_id', selectedTeamLeadId);
        if (selectedBatchName) q = q.eq('batch_name', selectedBatchName);

        const { data: chunk, error: chunkError } = await q.range(pageFrom, pageFrom + pageSize - 1);
        if (chunkError) throw chunkError;
        if (!chunk || chunk.length === 0) break;
        all.push(...chunk);
        if (chunk.length < pageSize) break;
        pageFrom += pageSize;
      }

      const { from, to } = parseLocalDayRange(fromDate, toDate);
      const byTc = new Map();

      const touch = (id) => {
        const key = String(id);
        if (!byTc.has(key)) {
          byTc.set(key, {
            telecaller_id: id,
            assigned_count: 0,
            no_disposition_count: 0,
            disposed_count: 0,
            confirmed_count: 0,
            callback_count: 0,
            disposed_by_tc_count: 0,
            last_claimed_at: null,
            last_disposition_at: null,
          });
        }
        return byTc.get(key);
      };

      for (const l of all) {
        // Assigned / no disposition are counted on claim date
        const claimInRange = isWithinRange(l.claimed_at, from, to);
        const dispInRange = isWithinRange(l.disposition_updated_at, from, to);

        if (l.assigned_to && claimInRange) {
          const r = touch(l.assigned_to);
          r.assigned_count += 1;
          if (!l.disposition) r.no_disposition_count += 1;
        }

        if (l.assigned_to && l.disposition && dispInRange) {
          const r = touch(l.assigned_to);
          r.disposed_count += 1;
          const dispNorm = String(l.disposition || '').trim().toLowerCase();
          if (dispNorm === 'confirmed' || dispNorm === 'tl_confirmed' || dispNorm === 'tl confirmed' || dispNorm === 'tl-confirmed') {
            r.confirmed_count += 1;
          }
          if (l.disposition === 'callback') r.callback_count += 1;
        }

        if (l.disposition_updated_by && dispInRange) {
          const r = touch(l.disposition_updated_by);
          r.disposed_by_tc_count += 1;
          if (!r.last_disposition_at || new Date(l.disposition_updated_at) > new Date(r.last_disposition_at)) {
            r.last_disposition_at = l.disposition_updated_at;
          }
        }

        if (l.claimed_by && claimInRange) {
          const r = touch(l.claimed_by);
          if (!r.last_claimed_at || new Date(l.claimed_at) > new Date(r.last_claimed_at)) {
            r.last_claimed_at = l.claimed_at;
          }
        }
      }

      const computed = Array.from(byTc.values()).sort((a, b) => {
        if (b.no_disposition_count !== a.no_disposition_count) return b.no_disposition_count - a.no_disposition_count;
        if (b.assigned_count !== a.assigned_count) return b.assigned_count - a.assigned_count;
        return (b.disposed_count || 0) - (a.disposed_count || 0);
      });

      setRows(computed);
    } catch (e) {
      console.error('Failed to load WFH employee stats:', e);
      alert(e?.message || 'Failed to load WFH employee stats');
      setRows([]);
    } finally {
      setIsLoadingRows(false);
    }
  };

  useEffect(() => {
    loadBatches();
    setRows([]);
  }, [selectedTeamLeadId]);

  useEffect(() => {
    loadRows();
  }, [selectedBatchName, selectedTeamLeadId, fromDate, toDate]);

  useEffect(() => {
    const known = new Set([
      ...telecallers.map((u) => String(u.id)),
      ...teamLeads.map((u) => String(u.id)),
      ...resolvedUsersById.keys(),
    ]);
    const missing = Array.from(
      new Set(
        (rows || [])
          .map((r) => String(r.telecaller_id || '').trim())
          .filter((id) => id && !known.has(id)),
      ),
    );
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const found = new Map();
      await Promise.all(
        missing.map(async (id) => {
          try {
            const { data, error } = await supabase.rpc('admin_get_user_by_id', {
              p_user_id: id,
            });
            if (error) throw error;
            const row = Array.isArray(data) ? data[0] : data;
            if (row?.id) {
              found.set(String(row.id), {
                id: row.id,
                name: row.name || null,
                email: row.email || null,
                phone: row.phone || null,
              });
              return;
            }
          } catch {
            /* try direct users read */
          }
          try {
            const { data } = await supabase
              .from('users')
              .select('id,name,email,phone')
              .eq('id', id)
              .maybeSingle();
            if (data?.id) {
              found.set(String(data.id), data);
            }
          } catch {
            /* leave unresolved */
          }
        }),
      );
      if (cancelled || found.size === 0) return;
      setResolvedUsersById((prev) => {
        const next = new Map(prev);
        found.forEach((u, id) => next.set(id, u));
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [rows, telecallers, teamLeads, resolvedUsersById]);

  // Per-telecaller call stats (Sarv + Tata) for the selected TL & date range
  const { data: callStatsByTc = {} } = useQuery({
    queryKey: ['wfhEmployeeCallStats', selectedTeamLeadId, fromDate, toDate],
    enabled: !!selectedTeamLeadId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_wfh_employee_call_stats', {
        p_team_lead_id: selectedTeamLeadId,
        p_from_date: fromDate || null,
        p_to_date: toDate || null,
      });
      if (error) {
        console.warn('admin_wfh_employee_call_stats failed:', error);
        return {};
      }
      const map = {};
      (data || []).forEach((r) => {
        if (!r?.telecaller_id) return;
        map[String(r.telecaller_id)] = {
          total_calls: Number(r.total_calls) || 0,
          answered_calls: Number(r.answered_calls) || 0,
          missed_calls: Number(r.missed_calls) || 0,
          sarv_calls: Number(r.sarv_calls) || 0,
          tata_calls: Number(r.tata_calls) || 0,
          total_talk_seconds: Number(r.total_talk_seconds) || 0,
          last_call_at: r.last_call_at || null,
        };
      });
      return map;
    },
  });

  const enrichedRows = useMemo(() => {
    return (rows || []).map((r) => {
      const id = String(r.telecaller_id || '');
      const u = telecallersById.get(id);
      const cs = callStatsByTc[id] || {};
      const name = (u?.name || '').trim();
      const email = (u?.email || '').trim();
      const shortId = id ? `${id.slice(0, 8)}…` : '—';
      return {
        ...r,
        _name: name || email || (id ? `Unknown TC (${shortId})` : '—'),
        _email: email,
        _phone: u?.phone || u?.contact_number || '',
        _unresolved: !name && !email && !!id,
        _total_calls: cs.total_calls || 0,
        _answered_calls: cs.answered_calls || 0,
        _sarv_calls: cs.sarv_calls || 0,
        _tata_calls: cs.tata_calls || 0,
      };
    });
  }, [rows, telecallersById, callStatsByTc]);

  const totals = useMemo(() => {
    const list = enrichedRows || [];
    return {
      assigned_count: list.reduce((s, r) => s + (Number(r.assigned_count) || 0), 0),
      no_disposition_count: list.reduce((s, r) => s + (Number(r.no_disposition_count) || 0), 0),
      disposed_count: list.reduce((s, r) => s + (Number(r.disposed_count) || 0), 0),
      confirmed_count: list.reduce((s, r) => s + (Number(r.confirmed_count) || 0), 0),
      callback_count: list.reduce((s, r) => s + (Number(r.callback_count) || 0), 0),
      disposed_by_tc_count: list.reduce((s, r) => s + (Number(r.disposed_by_tc_count) || 0), 0),
      total_calls: list.reduce((s, r) => s + (Number(r._total_calls) || 0), 0),
      answered_calls: list.reduce((s, r) => s + (Number(r._answered_calls) || 0), 0),
      sarv_calls: list.reduce((s, r) => s + (Number(r._sarv_calls) || 0), 0),
      tata_calls: list.reduce((s, r) => s + (Number(r._tata_calls) || 0), 0),
    };
  }, [enrichedRows]);

  const buildDashboardUrl = () => {
    const params = new URLSearchParams();
    if (selectedTeamLeadId) params.set('tl', selectedTeamLeadId);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    return `/wfh-dashboard?${params.toString()}`;
  };

  const buildTcDetailUrl = (r) => {
    const params = new URLSearchParams();
    if (selectedTeamLeadId) params.set('teamLeadId', selectedTeamLeadId);
    if (selectedBatchName) params.set('batch', selectedBatchName);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    if (r?._name) params.set('name', r._name);
    if (r?._email) params.set('email', r._email);
    return `/wfh-employee-report/telecaller/${encodeURIComponent(String(r.telecaller_id))}?${params.toString()}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">WFH Employee Report</h1>
          <p className="text-gray-600 mt-1">Telecaller-wise WFH lead status (assigned/disposed/no-disposition/callback)</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={buildDashboardUrl()}
            className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
            title="Open WFH Dashboard for selected date range"
          >
            <LayoutDashboard size={16} />
            <span>Dashboard</span>
          </Link>
          <button
            onClick={loadRows}
            className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
            disabled={!selectedTeamLeadId || isLoadingRows}
          >
            <RefreshCw size={16} />
            <span>{isLoadingRows ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Lead</label>
            <select
              value={selectedTeamLeadId}
              onChange={(e) => setSelectedTeamLeadId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              disabled={isLoadingUsers}
            >
              <option value="">{isLoadingUsers ? 'Loading...' : 'Select Team Lead'}</option>
              {teamLeads.map((tl) => (
                <option key={tl.id} value={tl.id}>
                  {tl.name || tl.email || tl.id}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Batch (optional)</label>
            <select
              value={selectedBatchName}
              onChange={(e) => setSelectedBatchName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              disabled={!selectedTeamLeadId}
            >
              <option value="">{selectedTeamLeadId ? 'All batches' : 'Select Team Lead first'}</option>
              {batches.map((b) => (
                <option key={b.batch_name} value={b.batch_name}>
                  {b.batch_name} (disposed: {b.disposed_count}/{b.total_count})
                </option>
              ))}
            </select>
            <div className="text-xs text-gray-500 mt-1">Choose a batch for detailed employee-wise stats, or keep All batches.</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              disabled={!selectedTeamLeadId}
              max={toDate || undefined}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              disabled={!selectedTeamLeadId}
              min={fromDate || undefined}
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const t = toYMD(new Date());
                  setFromDate(t);
                  setToDate(t);
                }}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                disabled={!selectedTeamLeadId}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  // last 7 days (incl today)
                  const end = new Date();
                  const start = new Date();
                  start.setDate(end.getDate() - 6);
                  setFromDate(toYMD(start));
                  setToDate(toYMD(end));
                }}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                disabled={!selectedTeamLeadId}
              >
                Last 7 days
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Telecaller-wise Stats</h2>
          </div>
          <div className="text-sm text-gray-600">{selectedTeamLeadId ? `${enrichedRows.length} employees` : 'Select Team Lead'}</div>
        </div>

        {!selectedTeamLeadId ? (
          <div className="text-gray-600">Select a Team Lead to view employee-wise WFH details.</div>
        ) : isLoadingRows ? (
          <div className="text-gray-600">Loading...</div>
        ) : enrichedRows.length === 0 ? (
          <div className="text-gray-600">No data found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telecaller</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">No Disposition</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Disposed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Confirmed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Callback</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" title="All calls (Sarv + Tata) for the date range">Calls</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" title="Calls that connected">Answered</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sarv</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Tata</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Claimed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Disposition</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {enrichedRows.map((r) => {
                  const totalCalls = Number(r._total_calls) || 0;
                  const answered = Number(r._answered_calls) || 0;
                  const ansPct = totalCalls ? Math.round((answered / totalCalls) * 100) : 0;
                  return (
                    <tr key={String(r.telecaller_id)} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Link
                          to={buildTcDetailUrl(r)}
                          className="font-medium text-primary-700 hover:text-primary-800 hover:underline"
                          title={r._unresolved ? `User id: ${r.telecaller_id}` : 'Open detailed report'}
                        >
                          {fmt(r._name)}
                        </Link>
                        <div className="text-xs text-gray-500">
                          {r._email ? r._email : r._phone ? fmt(r._phone) : r._unresolved ? 'User not in telecaller list (left / role changed)' : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{fmt(r.assigned_count)}</td>
                      <td className="px-4 py-3 text-right font-medium text-amber-700">{fmt(r.no_disposition_count)}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{fmt(r.disposed_count)}</td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-700">{fmt(Number(r.confirmed_count) || 0)}</td>
                      <td className="px-4 py-3 text-right font-medium text-blue-700">{fmt(r.callback_count)}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{totalCalls}</td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-700">
                        {answered}
                        {totalCalls > 0 && (
                          <span className="block text-[10px] text-gray-400 font-normal">{ansPct}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-purple-700 font-medium">{Number(r._sarv_calls) || 0}</td>
                      <td className="px-4 py-3 text-right text-sm text-orange-700 font-medium">{Number(r._tata_calls) || 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{fmtDateTime(r.last_claimed_at)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{fmtDateTime(r.last_disposition_at)}</td>
                    </tr>
                  );
                })}
                {(enrichedRows?.length || 0) > 0 && (
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{fmt(totals.assigned_count)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{fmt(totals.no_disposition_count)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{fmt(totals.disposed_count)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{fmt(totals.confirmed_count)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{fmt(totals.callback_count)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{totals.total_calls}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{totals.answered_calls}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{totals.sarv_calls}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{totals.tata_calls}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">—</td>
                    <td className="px-4 py-3 text-sm text-gray-700">—</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WFHEmployeeReport;

